import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { FiCalendar, FiActivity, FiExternalLink } from 'react-icons/fi';
import api from '../../api/axiosInstance';

const DetailedUsageReport = () => {
    const { year, month } = useParams();
    const [dailyData, setDailyData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [excludeZeroDays, setExcludeZeroDays] = useState(true);
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();

    useEffect(() => {
        window.scrollTo(0, 0);
        setBreadcrumb(['Countdown', 'Billing', 'Usage Report']);
        setBreadcrumbLink(['/countdown', '/countdown/billing']);

        const fetchDailyData = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/logs/stats/daily/${year}/${month}`);
                setDailyData(res.data);
            } catch (err) {
                console.error("Failed to fetch daily report:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDailyData();
    }, [year, month, setBreadcrumb, setBreadcrumbLink]);

    const chartData = useMemo(() => ({
        labels: dailyData.map(d => d.date.split('-')[2]), // Just the day number
        datasets: [{
            label: 'Requests',
            data: dailyData.map(d => d.requests),
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderRadius: 4,
        }]
    }), [dailyData]);

    const totalRequests = dailyData.reduce((acc, curr) => acc + curr.requests, 0);

    // Smart average calculation
    const dailyAverage = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Filter out future dates
        const pastDays = dailyData.filter(d => new Date(d.date) <= today);

        // Optionally filter out zero days
        const relevantDays = excludeZeroDays
            ? pastDays.filter(d => d.requests > 0)
            : pastDays;

        if (relevantDays.length === 0) return 0;

        const total = relevantDays.reduce((acc, curr) => acc + curr.requests, 0);
        return Math.round(total / relevantDays.length);
    }, [dailyData, excludeZeroDays]);

    // Projected usage calculation
    const projectedUsage = useMemo(() => {
        if (dailyAverage === 0 || dailyData.length === 0) return totalRequests;

        const today = new Date();
        const reportMonth = new Date(year, month - 1);

        // If viewing a past month, just return total
        if (reportMonth.getFullYear() < today.getFullYear() ||
            (reportMonth.getFullYear() === today.getFullYear() && reportMonth.getMonth() < today.getMonth())) {
            return totalRequests;
        }

        // For current month, project based on daily average
        const daysInMonth = new Date(year, month, 0).getDate();
        return Math.round(dailyAverage * daysInMonth);
    }, [dailyAverage, dailyData.length, totalRequests, year, month]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-brand-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading flex items-center gap-2">
                    <FiCalendar /> Usage Report: {new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h1>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-5 rounded-lg border border-brand-border">
                    <p className="text-sm text-brand-paragraph">Total Requests</p>
                    <p className="text-2xl font-bold text-brand-heading">{totalRequests.toLocaleString()}</p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-brand-border">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-brand-paragraph">Daily Average</p>
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={excludeZeroDays}
                                onChange={(e) => setExcludeZeroDays(e.target.checked)}
                                className="w-3 h-3"
                            />
                            <span className="text-gray-500">Exclude 0s</span>
                        </label>
                    </div>
                    <p className="text-2xl font-bold text-brand-heading">{dailyAverage.toLocaleString()}</p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-brand-border">
                    <p className="text-sm text-brand-paragraph">Projected Usage</p>
                    <p className="text-2xl font-bold text-brand-heading">{projectedUsage.toLocaleString()}</p>
                </div>
            </div>

            {/* Daily Chart */}
            <div className="bg-white p-6 rounded-lg border border-brand-border shadow-sm mb-8">
                <h2 className="text-lg font-bold text-brand-heading mb-4 flex items-center gap-2">
                    <FiActivity /> Daily Traffic
                </h2>
                <div className="h-80">
                    <Bar
                        data={chartData}
                        options={{
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: false }
                            },
                            scales: {
                                y: { beginAtZero: true }
                            }
                        }}
                    />
                </div>
            </div>

            {/* Daily Table */}
            <div className="bg-white rounded-lg border border-brand-border shadow-sm">
                <div className="p-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-brand-heading">Daily Breakdown</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-brand-paragraph">
                        <thead className="text-xs text-brand-heading uppercase bg-gray-50">
                            <tr>
                                <th className="py-3 px-6">Date</th>
                                <th className="py-3 px-6">Total Requests</th>
                                <th className="py-3 px-6 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dailyData.map((day) => (
                                <tr key={day.date} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-4 px-6 font-medium text-brand-heading">
                                        {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="py-4 px-6">{day.requests.toLocaleString()}</td>
                                    <td className="py-4 px-6 text-right">
                                        <a
                                            href={`/countdown/analytics/logs?startDate=${day.date}&endDate=${day.date}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-brand-accent hover:text-blue-700 font-medium"
                                        >
                                            View Logs <FiExternalLink />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DetailedUsageReport;
